import { type RegisteredRouter, type RouteById } from '@tanstack/react-router';
import { z } from 'zod';
import {
  DISTANCES,
  PAYLOAD_INDEX_TYPES,
  PRECISION_TIERS,
  type SpaceCollectionCreateConfig,
  TOKENIZERS,
} from '@/api/services/serverless/space-collections';
import { collectionNameSchema } from './collection-name-schema';

// The create form covers what the serverless API accepts and nothing else.

// The collection manager's create limits (`qdrant-serverless` `collection-manager/src/limits.rs`); no environment
// overrides them. The REST schema still documents 65536 as the dense maximum, which the planner refuses.
export const MAX_DENSE_SIZE = 4096;
export const MAX_MULTIVECTOR_SIZE = 512;
export const MAX_VECTOR_FIELDS = 10;
export const MAX_PAYLOAD_INDEXES = 10;
const MAX_FIELD_PATH_BYTES = 128;
// `check_collection_name` takes the characters the public API does, but not a leading '.' or '-'.
const NAME_START = /^[^.-]/;
const WHOLE_NUMBER = /^\d+$/;

export const SEARCH_TEMPLATES = ['single', 'hybrid', 'custom'] as const;
type SearchTemplate = (typeof SEARCH_TEMPLATES)[number];

// The engine's `Language` and `SnowballLanguage` (`qdrant/lib/segment/src/data_types/index.rs`); the bridge takes any string.
export const STOPWORD_LANGUAGES = [
  'arabic',
  'azerbaijani',
  'basque',
  'bengali',
  'catalan',
  'chinese',
  'danish',
  'dutch',
  'english',
  'finnish',
  'french',
  'german',
  'greek',
  'hebrew',
  'hinglish',
  'hungarian',
  'indonesian',
  'italian',
  'japanese',
  'kazakh',
  'nepali',
  'norwegian',
  'portuguese',
  'romanian',
  'russian',
  'slovene',
  'spanish',
  'swedish',
  'tajik',
  'turkish',
] as const;
export const STEMMER_LANGUAGES = [
  'arabic',
  'armenian',
  'danish',
  'dutch',
  'english',
  'finnish',
  'french',
  'german',
  'greek',
  'hungarian',
  'italian',
  'norwegian',
  'portuguese',
  'romanian',
  'russian',
  'spanish',
  'swedish',
  'tamil',
  'turkish',
] as const;

// A text index without a stemmer leaves `stemmer` out of the request; empty, as a cleared autocomplete leaves it.
const NO_STEMMER = '';
const STEMMERS = [NO_STEMMER, ...STEMMER_LANGUAGES] as const;

// Numbers stay strings while typed, so an empty field is a validation error rather than a 0.
const denseVectorSchema = z
  .object({
    name: z.string().trim(),
    size: z.string().regex(WHOLE_NUMBER, 'Size must be a whole number'),
    distance: z.enum(DISTANCES),
    multivector: z.boolean(),
    precision: z.enum(PRECISION_TIERS),
  })
  // A multivector point holds its size once per sub-vector, so its ceiling is lower.
  .superRefine(({ size, multivector }, ctx) => {
    const max = multivector ? MAX_MULTIVECTOR_SIZE : MAX_DENSE_SIZE;
    if (WHOLE_NUMBER.test(size) && (Number(size) < 1 || Number(size) > max)) {
      ctx.addIssue({ code: 'custom', path: ['size'], message: `Size must be 1 to ${max}` });
    }
  });

const sparseVectorSchema = z.object({ name: z.string().trim(), idf: z.boolean(), precision: z.enum(PRECISION_TIERS) });

const tokenLengthSchema = z.string().regex(/^\d*$/, 'Must be a whole number');

const payloadIndexSchema = z.object({
  asciiFolding: z.boolean(),
  customStopwords: z.array(z.string()),
  field: z
    .string()
    .trim()
    .min(1, 'Field name is required')
    // The planner counts UTF-8 bytes, not characters.
    .refine(
      (field) => new TextEncoder().encode(field).length <= MAX_FIELD_PATH_BYTES,
      `Field path must be at most ${MAX_FIELD_PATH_BYTES} bytes`,
    ),
  lookup: z.boolean(),
  lowercase: z.boolean(),
  maxTokenLength: tokenLengthSchema,
  minTokenLength: tokenLengthSchema,
  phraseMatching: z.boolean(),
  prefix: z.boolean(),
  range: z.boolean(),
  stemmer: z.enum(STEMMERS),
  stopwordLanguages: z.array(z.enum(STOPWORD_LANGUAGES)),
  tokenizer: z.enum(TOKENIZERS),
  type: z.enum(PAYLOAD_INDEX_TYPES),
});

const newCollectionNameSchema = collectionNameSchema.regex(NAME_START, 'Cannot start with a period or hyphen');

type CollectionFields = {
  template: SearchTemplate;
  denseVectors: z.infer<typeof denseVectorSchema>[];
  sparseVectors: z.infer<typeof sparseVectorSchema>[];
  payloadIndexes: z.infer<typeof payloadIndexSchema>[];
};

// Dense and sparse vectors share one namespace; only the single template's vector goes unnamed.
function checkVectorNames({ template, denseVectors, sparseVectors }: CollectionFields, ctx: z.RefinementCtx) {
  const named = [
    ...(template === 'single' ? [] : denseVectors.map(({ name }, index) => ({ name, path: ['denseVectors', index] }))),
    ...sparseVectors.map(({ name }, index) => ({ name, path: ['sparseVectors', index] })),
  ];
  const seen = new Set<string>();
  for (const { name, path } of named) {
    if (!name) {
      ctx.addIssue({ code: 'custom', path: [...path, 'name'], message: 'Vector name is required' });
      continue;
    }
    if (seen.has(name)) {
      ctx.addIssue({ code: 'custom', path: [...path, 'name'], message: 'Vector names must be unique' });
    }
    seen.add(name);
  }
}

// Only the selected type's options are sent, so only they are checked.
function checkPayloadIndexes({ payloadIndexes }: CollectionFields, ctx: z.RefinementCtx) {
  const seen = new Set<string>();
  payloadIndexes.forEach(({ field, type, lookup, range, minTokenLength, maxTokenLength }, index) => {
    const path = ['payloadIndexes', index];
    if (seen.has(field)) {
      ctx.addIssue({ code: 'custom', path: [...path, 'field'], message: 'Fields must be unique' });
    }
    seen.add(field);
    if (type === 'integer' && !lookup && !range) {
      ctx.addIssue({
        code: 'custom',
        path: [...path, 'lookup'],
        message: 'Enable match filters, range filters, or both',
      });
    }
    if (type === 'text' && minTokenLength && maxTokenLength && Number(minTokenLength) > Number(maxTokenLength)) {
      ctx.addIssue({ code: 'custom', path: [...path, 'maxTokenLength'], message: 'Must not be less than the minimum' });
    }
  });
}

export const createCollectionSchema = z
  .object({
    name: newCollectionNameSchema,
    template: z.enum(SEARCH_TEMPLATES),
    denseVectors: z.array(denseVectorSchema).max(MAX_VECTOR_FIELDS, `At most ${MAX_VECTOR_FIELDS} dense vectors`),
    sparseVectors: z.array(sparseVectorSchema).max(MAX_VECTOR_FIELDS, `At most ${MAX_VECTOR_FIELDS} sparse vectors`),
    payloadIndexes: z
      .array(payloadIndexSchema)
      .max(MAX_PAYLOAD_INDEXES, `At most ${MAX_PAYLOAD_INDEXES} payload indexes`),
  })
  // The planner's rules (`collection-manager/src/planner/mod.rs`), so its 400 is a fallback, not the normal path.
  .superRefine((values, ctx) => {
    if (values.denseVectors.length + values.sparseVectors.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['denseVectors'], message: 'Add at least one dense or sparse vector' });
    }
    checkVectorNames(values, ctx);
    checkPayloadIndexes(values, ctx);
  });

export type CreateCollectionValues = z.input<typeof createCollectionSchema>;
type DenseVectorValues = CreateCollectionValues['denseVectors'][number];
type SparseVectorValues = CreateCollectionValues['sparseVectors'][number];
type PayloadIndexValues = CreateCollectionValues['payloadIndexes'][number];

export const newDenseVector = (): DenseVectorValues => ({
  name: '',
  size: '1536',
  distance: 'Cosine',
  multivector: false,
  precision: 'high',
});

export const newSparseVector = (): SparseVectorValues => ({ name: '', idf: false, precision: 'high' });

export const newPayloadIndex = (): PayloadIndexValues => ({
  asciiFolding: false,
  customStopwords: [],
  field: '',
  lookup: true,
  lowercase: true,
  maxTokenLength: '',
  minTokenLength: '',
  phraseMatching: true,
  prefix: false,
  range: true,
  stemmer: NO_STEMMER,
  stopwordLanguages: [],
  tokenizer: 'whitespace',
  type: 'keyword',
});

/** The vectors each template starts with. Hybrid is one dense and one sparse vector, both named. */
export const templateVectors = (template: SearchTemplate) => ({
  denseVectors: [newDenseVector()],
  sparseVectors: template === 'hybrid' ? [newSparseVector()] : [],
});

export const createCollectionDefaults: CreateCollectionValues = {
  name: '',
  template: 'single',
  ...templateVectors('single'),
  payloadIndexes: [],
};

/** What a link to the create page can preset: the name, the template, and the first dense vector's size and metric. */
type CreateCollectionPreset = RouteById<
  RegisteredRouter['routeTree'],
  '/_authenticated/_dashboard/accounts/$accountId/serverless-spaces/$spaceId/create-collection'
>['types']['fullSearchSchema'];

/** The form's defaults with a preset on top; whatever it leaves out keeps the template's own defaults. */
export function presetCollectionValues({
  name = createCollectionDefaults.name,
  template = createCollectionDefaults.template,
  size,
  distance,
}: CreateCollectionPreset): CreateCollectionValues {
  const { denseVectors, sparseVectors } = templateVectors(template);
  const presetVector = (vector: DenseVectorValues): DenseVectorValues => ({
    ...vector,
    size: size === undefined ? vector.size : String(size),
    distance: distance ?? vector.distance,
  });

  return {
    ...createCollectionDefaults,
    name,
    template,
    denseVectors: denseVectors.map((vector, index) => (index === 0 ? presetVector(vector) : vector)),
    sparseVectors,
  };
}

// An `undefined` property is left out of the JSON sent, so only what the user turned on is in the request.
const toDenseVector = ({ size, distance, multivector, precision }: DenseVectorValues) => ({
  size: Number(size),
  distance,
  precision,
  multivector: multivector || undefined,
});

const toTokenLength = (length: string) => (length ? Number(length) : undefined);

// Chips typed as blanks are dropped rather than refused.
const toStopwords = ({ stopwordLanguages, customStopwords }: PayloadIndexValues) => {
  // The API takes each stopword once.
  const custom = [...new Set(customStopwords.map((word) => word.trim()).filter(Boolean))];
  if (stopwordLanguages.length + custom.length === 0) {
    return undefined;
  }
  return {
    languages: stopwordLanguages.length > 0 ? stopwordLanguages : undefined,
    custom: custom.length > 0 ? custom : undefined,
  };
};

const isDefaultText = (index: PayloadIndexValues) =>
  index.tokenizer === 'whitespace' &&
  index.lowercase &&
  index.phraseMatching &&
  !index.asciiFolding &&
  !index.minTokenLength &&
  !index.maxTokenLength &&
  !toStopwords(index) &&
  index.stemmer === NO_STEMMER;

// The bare type name when every option is the API default, as the API itself writes it.
const toPayloadIndex = (index: PayloadIndexValues) => {
  if (index.type === 'keyword' && index.prefix) {
    return { type: index.type, prefix: true };
  }
  if (index.type === 'integer' && !(index.lookup && index.range)) {
    return { type: index.type, lookup: index.lookup, range: index.range };
  }
  if (index.type === 'text' && !isDefaultText(index)) {
    return {
      ascii_folding: index.asciiFolding || undefined,
      lowercase: index.lowercase,
      max_token_len: toTokenLength(index.maxTokenLength),
      min_token_len: toTokenLength(index.minTokenLength),
      phrase_matching: index.phraseMatching,
      stemmer: index.stemmer === NO_STEMMER ? undefined : { type: 'snowball' as const, language: index.stemmer },
      stopwords: toStopwords(index),
      tokenizer: index.tokenizer,
      type: index.type,
    };
  }
  return index.type;
};

// A name not typed yet previews as a placeholder; a valid form has every name, so none is ever sent.
const keyOf = (name: string, placeholder: string, index: number) => {
  const suffix = index > 0 ? `_${index + 1}` : '';
  return name.trim() || `{${placeholder}${suffix}}`;
};

/** The body of `PUT /collections/{name}`; also what the form previews while it is being filled in. */
export function toCreateCollectionConfig({
  template,
  denseVectors,
  sparseVectors,
  payloadIndexes,
}: CreateCollectionValues): SpaceCollectionCreateConfig {
  const [unnamedVector] = denseVectors;
  const vectors =
    template === 'single' && unnamedVector
      ? toDenseVector(unnamedVector)
      : Object.fromEntries(
          denseVectors.map((vector, index) => [keyOf(vector.name, 'dense_vector_name', index), toDenseVector(vector)]),
        );

  const sparse = Object.fromEntries(
    sparseVectors.map(({ name, idf, precision }, index) => [
      keyOf(name, 'sparse_vector_name', index),
      { modifier: idf ? ('idf' as const) : undefined, precision },
    ]),
  );
  const indexes = Object.fromEntries(
    payloadIndexes.map((index, position) => [keyOf(index.field, 'field_name', position), toPayloadIndex(index)]),
  );

  return {
    vectors: denseVectors.length > 0 ? vectors : undefined,
    sparse_vectors: sparseVectors.length > 0 ? sparse : undefined,
    payload_indexes: payloadIndexes.length > 0 ? indexes : undefined,
  };
}
